import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { useActiveSection } from '@/hooks/useActiveSection';

export interface TocItem {
  id: string;
  label: string;
  /** Nesting level: 0 (default) = top-level, 1 = sub-section, 2 = sub-sub, etc. */
  level?: number;
}

interface TableOfContentsProps {
  items: TocItem[];
}

export function TableOfContents({ items }: TableOfContentsProps) {
  const sectionIds = useMemo(() => items.map((i) => i.id), [items]);
  const activeId = useActiveSection(sectionIds);

  return (
    <aside className="hidden xl:block w-56 shrink-0">
      <nav className="sticky top-[4.5rem] space-y-0.5">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
          On this page
        </h4>
        {items.map((item) => {
          const isActive = activeId === item.id;
          const level = item.level ?? 0;
          return (
            <a
              key={item.id}
              href={`#${item.id}`}
              className={cn(
                'block text-xs py-1 transition-colors border-l-2',
                level === 0 && 'pl-3',
                level === 1 && 'pl-6',
                level >= 2 && 'pl-9',
                isActive
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400 font-medium'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:border-gray-300 dark:hover:border-gray-600'
              )}
            >
              {item.label}
            </a>
          );
        })}
      </nav>
    </aside>
  );
}
